import { useState, type ReactElement } from 'react';
import { Form, useActionData, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, invalidProps } from '@marketplace/ui';
import { type ClientProfile, type SavedAddressEntry } from '@marketplace/contracts';
import { type AccountActionData, type AddressFormField } from './actions.js';

/**
 * `W2-T04` §2 — the name providers see, and the address book, on `/:lang/account`.
 *
 * Every control is a `<Form method="post">` to the page's own action (`W2-T09`), so a write works
 * before the JavaScript has loaded and the list a person sees after it is the one the loader read
 * back. Inputs are uncontrolled, as in `ProviderForm`: a refused submit keeps what was typed.
 */

/** What the last submit said about *this* form, if anything. */
function resultFor(
  result: AccountActionData | undefined,
  intent: AccountActionData['intent'],
  addressId?: string,
): AccountActionData | undefined {
  if (result?.intent !== intent) return undefined;
  if (addressId !== undefined && result.addressId !== addressId) return undefined;
  return result;
}

export function ClientProfileForm({ profile }: { profile: ClientProfile }): ReactElement {
  const { t } = useTranslation();
  const result = resultFor(useActionData<AccountActionData>(), 'profile');
  const navigation = useNavigation();
  const error = result?.errors?.displayName;

  return (
    <Form
      method="post"
      className="mp-form mp-stack-form"
      data-testid="client-profile-form"
      noValidate
    >
      <input type="hidden" name="intent" value="profile" />
      <label htmlFor="client-displayName">{t('account.profile.displayName')}</label>
      <p className="mp-meta">{t('account.profile.hint')}</p>
      <input
        id="client-displayName"
        name="displayName"
        maxLength={80}
        autoComplete="name"
        defaultValue={profile.displayName ?? ''}
        {...invalidProps(error, 'client-displayName-error')}
      />
      <FieldError
        id="client-displayName-error"
        message={error === undefined ? undefined : t(error)}
      />
      {result?.failure === undefined ? null : <Alert tone="error">{t(result.failure)}</Alert>}
      {result?.saved === true ? <Alert tone="success">{t('account.profile.saved')}</Alert> : null}
      <Button
        type="submit"
        isPending={navigation.state === 'submitting'}
        pendingLabel={t('auth.pending')}
      >
        {t('account.profile.submit')}
      </Button>
    </Form>
  );
}

/** The fields an entry is made of, for adding one and for editing one. */
function AddressFields({
  idPrefix,
  initial,
  errors,
}: {
  idPrefix: string;
  initial?: SavedAddressEntry;
  errors: Partial<Record<AddressFormField, string>>;
}): ReactElement {
  const { t } = useTranslation();

  function field(
    name: AddressFormField,
    options: { maxLength: number; autoComplete: string; inputMode?: 'numeric' },
  ): ReactElement {
    const id = `${idPrefix}-${name}`;
    return (
      <>
        <label htmlFor={id}>{t(`account.addresses.field.${name}`)}</label>
        <input
          id={id}
          name={name}
          maxLength={options.maxLength}
          autoComplete={options.autoComplete}
          defaultValue={initial?.[name] ?? ''}
          {...(options.inputMode === undefined ? {} : { inputMode: options.inputMode })}
          {...invalidProps(errors[name], `${id}-error`)}
        />
        <FieldError
          id={`${id}-error`}
          message={errors[name] === undefined ? undefined : t(errors[name])}
        />
      </>
    );
  }

  return (
    <>
      {field('label', { maxLength: 60, autoComplete: 'off' })}
      {field('line1', { maxLength: 200, autoComplete: 'address-line1' })}
      {field('postalCode', { maxLength: 5, autoComplete: 'postal-code', inputMode: 'numeric' })}
      {field('city', { maxLength: 100, autoComplete: 'address-level2' })}
      {field('province', { maxLength: 100, autoComplete: 'address-level1' })}
    </>
  );
}

function EntryForm({ entry }: { entry: SavedAddressEntry }): ReactElement {
  const { t } = useTranslation();
  const result = resultFor(useActionData<AccountActionData>(), 'address-update', entry.id);

  return (
    <Form
      method="post"
      className="mp-form mp-stack-form"
      data-testid={`address-edit-form-${entry.id}`}
      noValidate
    >
      <input type="hidden" name="intent" value="address-update" />
      <input type="hidden" name="addressId" value={entry.id} />
      <AddressFields
        idPrefix={`address-${entry.id}`}
        initial={entry}
        errors={result?.errors ?? {}}
      />
      {result?.failure === undefined ? null : <Alert tone="error">{t(result.failure)}</Alert>}
      <Button type="submit" variant="primary">
        {t('account.addresses.update.submit')}
      </Button>
    </Form>
  );
}

/** One hidden-field form with one button: making an entry the default, or removing it. */
function EntryAction({
  entry,
  intent,
  label,
}: {
  entry: SavedAddressEntry;
  intent: 'address-default' | 'address-delete';
  label: string;
}): ReactElement {
  return (
    <Form method="post">
      <input type="hidden" name="intent" value={intent} />
      <input type="hidden" name="addressId" value={entry.id} />
      <Button type="submit" variant={intent === 'address-delete' ? 'danger' : 'secondary'}>
        {label}
      </Button>
    </Form>
  );
}

export function AddressBook({ addresses }: { addresses: SavedAddressEntry[] }): ReactElement {
  const { t } = useTranslation();
  const result = useActionData<AccountActionData>();
  const [editing, setEditing] = useState<string | null>(null);
  const added = resultFor(result, 'address-create');
  // A refusal of a removal or a default is about an entry that may no longer be listed, so it is
  // said once, above the list, rather than beside an entry that is gone.
  const entryFailure =
    (result?.intent === 'address-delete' || result?.intent === 'address-default') &&
    result.failure !== undefined
      ? result.failure
      : undefined;

  return (
    <div data-testid="address-book">
      {entryFailure === undefined ? null : <Alert tone="error">{t(entryFailure)}</Alert>}

      {addresses.length === 0 ? (
        <p className="mp-meta">{t('account.addresses.empty')}</p>
      ) : (
        <ul>
          {addresses.map((entry) => {
            const name = entry.label ?? t('account.addresses.defaultLabel');
            const open = editing === entry.id;
            return (
              <li key={entry.id}>
                <p>
                  <strong>{name}</strong>
                  {entry.isDefault ? (
                    <>
                      {' '}
                      <span className="mp-meta">{t('account.addresses.isDefault')}</span>
                    </>
                  ) : null}
                </p>
                <p>{`${entry.line1}, ${entry.postalCode} ${entry.city} (${entry.province})`}</p>
                <div>
                  {entry.isDefault ? null : (
                    <EntryAction
                      entry={entry}
                      intent="address-default"
                      label={t('account.addresses.makeDefault', { label: name })}
                    />
                  )}
                  <Button isExpanded={open} onPress={() => setEditing(open ? null : entry.id)}>
                    {t('account.addresses.edit', { label: name })}
                  </Button>
                  <EntryAction
                    entry={entry}
                    intent="address-delete"
                    label={t('account.addresses.remove', { label: name })}
                  />
                </div>
                {open ? <EntryForm entry={entry} /> : null}
              </li>
            );
          })}
        </ul>
      )}

      <h3 id="address-add">{t('account.addresses.add.title')}</h3>
      {/* Keyed on the list, so a successful add — which the loader reads back as a new entry —
          remounts the form empty instead of offering the same address again. */}
      <Form
        key={addresses.map((entry) => entry.id).join()}
        method="post"
        className="mp-form mp-stack-form"
        data-testid="address-add-form"
        aria-labelledby="address-add"
        noValidate
      >
        <input type="hidden" name="intent" value="address-create" />
        <AddressFields idPrefix="address-new" errors={added?.errors ?? {}} />
        {added?.failure === undefined ? null : <Alert tone="error">{t(added.failure)}</Alert>}
        <Button type="submit" variant="primary">
          {t('account.addresses.add.submit')}
        </Button>
      </Form>
    </div>
  );
}
